import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PencilSquareIcon,
  LockClosedIcon,
  LockOpenIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import {
  Table, THead, TBody, TR, TH, TD, ActionButton, EmptyRow,
} from "components/ui/Table";
import Select from "components/ui/Select";
import Pagination from "components/ui/Pagination";
import Modal from "components/ui/modals";
import ComposeMessage from "components/messages/ComposeMessage";
import { useAllMessages, useToggleMessageStatus } from "http-hooks/messages/useMessages";
const statusOptions = [
  { value: "", label: "All" },
  { value: "1", label: "Open" },
  { value: "0", label: "Closed" },
];

const Messages = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState({ status: "", page: 1, limit: 20 });
  const [showCompose, setShowCompose] = useState(false);

  const { data, isLoading, isFetching } = useAllMessages({
    status: filter.status,
    page: filter.page,
    limit: filter.limit,
  });

  const { mutateAsync: toggleStatus, isPending: isToggling } = useToggleMessageStatus();

  const rows = data?.rows || [];
  const totalPages = data?.totalPages || 1;
  const currentPage = data?.currentPage || 1;

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">Messages</h1>
          <p className="mt-2 text-sm text-gray-700">Internal company messages and notifications</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0">
          <button
            onClick={() => setShowCompose(true)}
            className="inline-flex items-center gap-2 rounded-md bg-gray-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilSquareIcon className="h-4 w-4" />
            Compose
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4 flex items-center gap-3">
        <div className="w-40">
          <Select
            options={statusOptions}
            value={statusOptions.find((o) => o.value === filter.status) || statusOptions[0]}
            onChange={(opt) => setFilter((f) => ({ ...f, status: opt?.value ?? "", page: 1 }))}
            label="Status"
          />
        </div>
      </div>

      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl overflow-hidden">
        <Table>
          <THead>
            <TR>
              <TH>#</TH>
              <TH>From</TH>
              <TH>To</TH>
              <TH>Subject</TH>
              <TH>Date</TH>
              <TH>Status</TH>
              <TH last>Actions</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading ? (
              <EmptyRow colSpan={7} message="Loading..." />
            ) : rows.length === 0 ? (
              <EmptyRow colSpan={7} message="No messages found" />
            ) : (
              rows.map((row, idx) => (
                <TR key={row.comment_id}>
                  <TD>{(currentPage - 1) * filter.limit + idx + 1}</TD>
                  <TD className="font-medium">{row.fromName}</TD>
                  <TD>{row.toName}</TD>
                  <TD>
                    <button
                      onClick={() => navigate(`/messages/${row.comment_id}`)}
                      className="text-left text-indigo-600 hover:text-indigo-500 font-medium"
                    >
                      {row.comment_subject}
                    </button>
                  </TD>
                  <TD>{formatDate(row.comment_timestamp)}</TD>
                  <TD>
                    {row.comment_status === 1 ? (
                      <CheckIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-500" />
                    )}
                  </TD>
                  <TD last>
                    <ActionButton
                      onClick={() => navigate(`/messages/${row.comment_id}`)}
                      variant="secondary"
                      title="View Thread"
                    >
                      <EnvelopeIcon className="h-4 w-4" />
                    </ActionButton>
                    <ActionButton
                      onClick={() => toggleStatus(row.comment_id)}
                      variant={row.comment_status === 1 ? "danger" : "primary"}
                      title={row.comment_status === 1 ? "Close Thread" : "Reopen Thread"}
                      className="mr-0"
                      disabled={isToggling}
                    >
                      {row.comment_status === 1 ? (
                        <LockClosedIcon className="h-4 w-4" />
                      ) : (
                        <LockOpenIcon className="h-4 w-4" />
                      )}
                    </ActionButton>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(p) => setFilter((f) => ({ ...f, page: p }))}
          />
        </div>
      )}

      {showCompose && (
        <Modal open={showCompose} setOpen={setShowCompose} maxWidth="md">
          <ComposeMessage onClose={() => setShowCompose(false)} />
        </Modal>
      )}
    </div>
  );
};

export default Messages;
